import styles from "./HomeBanner.module.css";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import Slider, { Settings } from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import CustomiseCta from "../CustomiseCta/CustomiseCta";

interface VideoLink {
  id: number;
  label: string;
  url: string;
  length: number;
}

interface Images {
  id: number;
  documentId: string;
  url: string;
  alternativeText: string;
}

interface BannerData {
  video_section: {
    title?: any;
    video_link: VideoLink | null;
    video_link_cta?: any[] | null;
    description?: any;
  };
  sliders: {
    id: number;
    desktop_img: Images;
    mobile_img: Images;
    ctas?: any[] | null;
    title: string;
    button_text: string;
  }[];
}

interface HeroBannerProps {
  data?: BannerData;
  popUpData?: any;
}

const HeroBanner = ({ data, popUpData }: HeroBannerProps) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isClient, setIsClient] = useState(false);

  const DEFAULT_DESKTOP_BANNER = "/assets/hero-video_frame.webp";
  const DEFAULT_MOBILE_BANNER = "/assets/home-def-mbl.webp";
  const DEFAULT_ALUMNI_DESKTOP_BANNER = "/assets/alumni-default-desktop.webp";
  const DEFAULT_ALUMNI_MOBILE_BANNER = "/assets/alumni-default-mobile.webp";

  const router = useRouter();
  const isAlumniPage = router?.pathname?.includes("/alumni") || false;

  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  // Optimize client-side initialization - detect mobile immediately
  useEffect(() => {
    setIsClient(true);
    
    // Initialize mobile state immediately without waiting
    const updateIsMobile = () => {
      if (typeof window !== "undefined") {
        setIsMobile(window?.matchMedia?.("(max-width: 760px)")?.matches || false);
      }
    };

    // Set initial state immediately
    updateIsMobile();
    
    // Add resize listener
    if (typeof window !== "undefined") {
      window?.addEventListener?.("resize", updateIsMobile);
      return () => window?.removeEventListener?.("resize", updateIsMobile);
    }
  }, []);

  const sliderSettings: Settings = useMemo(() => {
    const slidersCount = data?.sliders?.length || 0;
    return {
      dots: false,
      infinite: slidersCount > 1,
      swipe: slidersCount > 1,
      speed: 300,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: true,
      autoplaySpeed: 4000,
      arrows: slidersCount > 1,
      pauseOnHover: true,
      pauseOnFocus: true,
      lazyLoad: "ondemand",
      cssEase: "cubic-bezier(0.645, 0.045, 0.355, 1)",
    };
  }, [data?.sliders?.length]);

  const renderContent = useCallback(() => {
    const hasVideoContent = isClient && data?.video_section?.video_link?.url;
    const hasSliderContent = data?.sliders && data?.sliders?.length > 0;

    // Always use the hardcoded default image for initial render, with alumni support
    const defaultImage = isAlumniPage
      ? isMobile
        ? DEFAULT_ALUMNI_MOBILE_BANNER
        : DEFAULT_ALUMNI_DESKTOP_BANNER
      : isMobile
      ? DEFAULT_MOBILE_BANNER
      : DEFAULT_DESKTOP_BANNER;

    // Removed duplicate code block - content now shows immediately
    // The video/slider will render directly with poster image as fallback

    // Step 2: If video exists, show video (priority over sliders)
    if (hasVideoContent) {
      const posterImage = defaultImage;
      return (
        <div className={styles.ImageAuto}>
          {!isVideoLoaded && (
            <Image
              src={posterImage}
              alt="Video Placeholder"
              title="Video Placeholder"
              width={isMobile ? 800 : 1920}
              height={isMobile ? 400 : 880}
              sizes={isMobile ? "100vw" : "50vw"}
              style={{
                width: "100%",
                height: "auto",
                objectFit: "cover",
              }}
              priority
            />
          )}
          <div className={styles.overlay}></div>
          <div className={styles.callout}>
            {data?.video_section?.title && (
              <h1
                dangerouslySetInnerHTML={{
                  __html: data?.video_section?.title,
                }}
              />
            )}
            {data?.video_section?.description && (
              <p className={styles.desc}>{data?.video_section?.description}</p>
            )}{" "}
          </div>
          {data?.video_section?.video_link_cta && (
            <div className={styles.ButtonContainer}>
              <CustomiseCta
                ctaData={data?.video_section?.video_link_cta}
                popupData={popUpData}
              />
            </div>
          )}
          <video
            ref={videoRef}
            src={data?.video_section?.video_link?.url}
            autoPlay
            loop
            playsInline
            muted
            className={styles.video}
            preload={isMobile ? "metadata" : "auto"}
            poster={posterImage}
            onLoadedData={() => setIsVideoLoaded(true)}
            style={{
              width: "100%",
              objectFit: "cover",
              display: isVideoLoaded ? "block" : "none",
            }}
          />
        </div>
      );
    }

    // Step 3: If no video but sliders exist, show sliders directly
    if (hasSliderContent) {
      return (
        <Slider {...sliderSettings} className={styles.swiperSlilk}>
          {data?.sliders?.map((slider, index) => (
            <div
              key={slider?.id}
              className={styles.ImageAuto}
              style={{ cursor: "pointer" }}
            >
              <div className={styles.callout}>
                <h1>{slider?.title}</h1>
              </div>
              {slider?.ctas && Array.isArray(slider?.ctas) && (
                <div className={styles.ButtonContainer}>
                  {slider?.ctas?.map((cta, idx) => (
                    <CustomiseCta
                      key={cta?.id || idx}
                      ctaData={cta}
                      popupData={popUpData}
                    />
                  ))}{" "}
                </div>
              )}
              <Image
                src={
                  isMobile ? slider?.mobile_img?.url : slider?.desktop_img?.url
                }
                alt={slider?.desktop_img?.alternativeText || "Carousel image"}
                title={slider?.desktop_img?.alternativeText || "Carousel image"}
                width={isMobile ? 800 : 1920}
                height={isMobile ? 400 : 880}
                sizes={isMobile ? "100vw" : "50vw"}
                style={{
                  width: "100%",
                  height: "auto",
                  objectFit: "cover",
                }}
                priority={index === 0}
                fetchPriority={index === 0 ? "high" : "auto"}
                placeholder="blur"
                blurDataURL={
                  (isMobile
                    ? slider?.mobile_img?.url
                    : slider?.desktop_img?.url) + "?w=10&q=10"
                }
              />
            </div>
          ))}{" "}
        </Slider>
      );
    }

    // Step 4: Fallback: show default image if no video and no sliders
    return (
      <div className={styles.ImageAuto}>
        <Image
          src={defaultImage}
          alt="Hero Banner Default"
          title="Hero Banner Default"
          width={isMobile ? 800 : 1920}
          height={isMobile ? 400 : 880}
          sizes={isMobile ? "100vw" : "50vw"}
          style={{
            width: "100%",
            height: "auto",
            objectFit: "cover",
          }}
          priority
          fetchPriority="high"
        />
      </div>
    );
  }, [data, isMobile, isClient, sliderSettings, isVideoLoaded, isAlumniPage, popUpData]);

  return (
    <section className={styles.homebannerheight}>
      <section className={styles.heroVideo} data-section="Home Banner">
        <div className={styles.videoContainer}>{renderContent()}</div>
      </section>
    </section>
  );
};

export default HeroBanner;
